import {Injectable} from '@angular/core';
import {Transport} from 'mediasoup-client/lib/Transport';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {from, Observable, ReplaySubject, Subscription, switchMap, throwError} from 'rxjs';
import {DeviceId} from '@webmate-web-core/shared/types/ids';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {catchError, filter, map, take, tap, timeout} from 'rxjs/operators';
import {Device as MDevice} from 'mediasoup-client';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import {WebSocketSubject} from 'rxjs/internal/observable/dom/WebSocketSubject';

@Injectable()
export class AudioStreamingService {

    // TODO error handling (proper cache resetting)

    audioStreamingData: {
        [d: DeviceId]: {
            transport?: Transport<any>,
            connectCallback?: any;
        }
    } = {};

    mediaStreamSubject: { [d: DeviceId]: ReplaySubject<MediaStream> } = {};

    wsSubject: { [d: DeviceId]: WebSocketSubject<any> } = {};
    wsSubscription: { [d: DeviceId]: Subscription } = {};

    constructor(private deviceService: DeviceService, private authService: AuthService) {
        this.initPinger();
    }

    initPinger() {
        setInterval(() => {
            for (const subj of Object.values(this.wsSubject)) {
                if (!subj.closed) {
                    subj.next({type: 'ping'});
                }
            }
        }, 20000);
    }

    setupAudioStreaming(deviceId: DeviceId, timeoutMs: number = 20000): Observable<void> {
        return this.deviceService.imposeDevicePropertyRequirement(deviceId, DevicePropertyNames.AudioStreamingRequested, true).pipe(
            switchMap(() => this.deviceService.getDevice$(deviceId).pipe(
                filter(device => device.properties[DevicePropertyNames.AudioStreamingInfo]?.streamingActive === true),
                take(1), // we are only interested in the first result with activeStreaming
                map(() => {
                }), // map to void
                timeout(timeoutMs),
                catchError(err => throwError(() => 'Start audio streaming has timed out'))
            ))
        );
    }

    tearDownAudioStreaming(deviceId: DeviceId, timeoutMs: number = 20000): Observable<void> {
        this.cleanupDeviceStreamingResources(deviceId);
        return this.deviceService.imposeDevicePropertyRequirement(deviceId, DevicePropertyNames.AudioStreamingRequested, false).pipe(
            switchMap(() => this.deviceService.getDevice$(deviceId).pipe(
                filter(device => device.properties[DevicePropertyNames.AudioStreamingInfo]?.streamingActive === false),
                take(1), // we are only interested in the first result with activeStreaming
                map(() => {
                }), // map to void
                timeout(timeoutMs),
                catchError(err => throwError(() => 'Stop audio streaming has timed out'))
            ))
        );
    }

    resetAudioStream(deviceId: DeviceId): void {
        this.cleanupDeviceStreamingResources(deviceId);
    }

    startAudioStream(deviceId: DeviceId): Observable<MediaStream> {
        if (!this.mediaStreamSubject[deviceId]) {
            this.cleanupDeviceStreamingResources(deviceId);
            this.mediaStreamSubject[deviceId] = new ReplaySubject<MediaStream>(1);
            this.initWebRTC(deviceId);
        }
        return this.mediaStreamSubject[deviceId].asObservable();
    }

    private handleGetRouterRtpCapabilities(deviceId: DeviceId, device: MDevice, routerRtpCapabilities: any) {
        from(device.load({routerRtpCapabilities})).subscribe({
            next: () => {
                // request 2
                this.wsSubject[deviceId].next({type: 'createConsumerTransport'});
            },
            error: err => {
                console.error('error', err);
            },
        });
    }

    private handleConsumerTransportCreated(deviceId: DeviceId, device: MDevice, consumerTransportOptions: any) {
        const transport = device.createRecvTransport(consumerTransportOptions);
        if (!this.audioStreamingData[deviceId]) {
            this.audioStreamingData[deviceId] = {};
        }
        this.audioStreamingData[deviceId].transport = transport;
        // response 4
        transport.on('connect', ({dtlsParameters}, callback, errback) => {
            this.audioStreamingData[deviceId].connectCallback = callback;
            // request 5
            this.wsSubject[deviceId].next({
                type: 'connectConsumerTransport',
                data: {
                    transportId: transport.id,
                    dtlsParameters
                }
            });
        });

        transport.on('connectionstatechange', (state) => {
            switch (state) {
                case 'connected':
                    // request 6 => starts stream
                    this.wsSubject[deviceId].next({type: 'resume'});
                    break;
                case 'failed':
                    transport.close();
                    break;
                default:
                    break;
            }
        });
        // request 3
        this.wsSubject[deviceId].next({type: 'consume', data: device.rtpCapabilities});
    }

    private handleGotConsumeResponse(deviceId: DeviceId, data: any) {
        const {id, producerId, kind, rtpParameters} = data;
        if (!this.audioStreamingData[deviceId].transport) {
            console.error('No transport');
            return;
        }
        const codecOptions = {};
        // request 4
        from(this.audioStreamingData[deviceId].transport!.consume({producerId, id, kind, rtpParameters, appData: codecOptions})).subscribe({
            next: (consumer) => {
                // response 5.2
                const stream = new MediaStream();
                stream.addTrack(consumer.track);
                this.mediaStreamSubject[deviceId]?.next(stream);
            },
            error: err => {
                console.error('error', err);
            }
        });
    }

    private initWebRTC(deviceId: DeviceId) {
        const device = new MDevice();

        this.wsSubject[deviceId] = new WebSocketSubject<any>({
            url: this.authService.makeAuthorizedWebSocketUrl(this.deviceService.getDeviceAudioWebRtcSignalingURL(deviceId)),
            openObserver: {
                next: () => {
                    // request 1
                    this.wsSubject[deviceId].next({type: 'getRouterRtpCapabilities'});
                }
            },
        });

        // TODO subscription handling
        // TODO error handling
        this.wsSubscription[deviceId] = this.wsSubject[deviceId].pipe(tap(data => {
            if (data.type === 'getRouterRtpCapabilitiesResult') {
                // response 1
                this.handleGetRouterRtpCapabilities(deviceId, device, data.data);
            } else if (data.type === 'createConsumerTransportResult') {
                // response 2
                this.handleConsumerTransportCreated(deviceId, device, data.data);
            } else if (data.type === 'consumeResult') {
                // response 3
                this.handleGotConsumeResponse(deviceId, data.data);
            } else if (data.type === 'consumerTransportConnected') {
                // response 5.1
                this.audioStreamingData[deviceId]?.connectCallback?.();
            } else {
                console.warn(`Got unknown message of type ${data.type}`);
            }
        })).subscribe();
    }

    private cleanupDeviceStreamingResources(deviceId: DeviceId) {
        if (this.mediaStreamSubject[deviceId]) {
            this.mediaStreamSubject[deviceId].complete();
            delete this.mediaStreamSubject[deviceId];
        }

        if (this.wsSubscription[deviceId]) {
            this.wsSubscription[deviceId].unsubscribe();
            delete this.wsSubscription[deviceId];
        }

        if (this.wsSubject[deviceId]) {
            this.wsSubject[deviceId].complete();
            delete this.wsSubject[deviceId];
        }

        if (this.audioStreamingData[deviceId]?.transport) {
            this.audioStreamingData[deviceId].transport!.close();
        }
        delete this.audioStreamingData[deviceId];
    }


}
